import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import CvIcon from 'assets/CvIcon';
import ThreeDIcon from 'assets/ThreeDIcon';
import ButtonIcon from 'components/ButtonIcon';
import ButtonIconSvg from 'components/ButtonIconSvg';
import NewFeatureBadge from 'components/NewFeatureBadge';
import CustomTableCell from 'components/CustomTableCell';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import { InspectionStatusBadge } from 'components/RevisionRowData';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import AuthContext from 'context/AuthContext';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useContext, useMemo, useState, useEffect, startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { downloadValidationReport, getUserInspectionDashboard, getUserInspectionStats } from 'services/inspections-service';
import { generateViewerUrl, getFeatures } from 'services/revisions';
import { isCvViewerEnabled } from 'utils/cvViewer';
import { isShgcViewerEnabled } from 'utils/shgcViewer';

// Generic Status Card Component
interface StatusCardProps {
  backgroundColor: string;
  title: string;
  value: number;
}

const StatusCard = ({ backgroundColor, title, value }: StatusCardProps) => (
  <Card
    sx={{
      backgroundColor,
      color: 'white',
      boxShadow: 'none'
    }}
  >
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h6" component="div" sx={{ fontSize: '18px' }}>
        {title}
      </Typography>
      <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', mt: 1, fontSize: '36px' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const statusFilterOptions = (t: any) => [
  { value: '', label: t('ALL') || 'All' },
  { value: 'not_started', label: t('REVISION_INSPECTION_STATUS_NOT_STARTED') },
  { value: 'checked', label: t('REVISION_INSPECTION_STATUS_CHECKED') },
  { value: 'pending', label: t('REVISION_INSPECTION_STATUS_PENDING') }
]

export default function MyChecksPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [sortBy, setSortBy] = useState('updatedAt')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const { auth, organizationId } = useContext(AuthContext)
  const { enqueueSnackbar } = useSnackbar()
  const { t, i18n } = useTranslation()
  const isRtl = i18n.language === 'he'

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0)
  }, [status, searchQuery])

  const { data: dashboardData, isLoading, refetch } = useQuery(
    ['my-checks-dashboard', status, page, rowsPerPage, sortBy, sortOrder, searchQuery],
    () => getUserInspectionDashboard({
      status: status || undefined,
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy,
      sortOrder,
      search: searchQuery.trim() || undefined
    })
  )

  const { data: statusStatsData } = useQuery(
    ['my-checks-stats'],
    getUserInspectionStats
  )

  const { data: features } = useQuery('features', getFeatures)

  const inspections = dashboardData?.data || []
  const totalCount = dashboardData?.paging?.total ?? dashboardData?.totalCount ?? 0

  const allRows = useMemo(() => (inspections || []).map((it: any) => ({
    id: it._id || it.id, // assignment id
    organizationName: it.organization?.company_name || '-',
    revisionId: it.revision?._id || it.revision?.id,
    projectId: it.project?._id || it.project?.id,
    projectName: it.project?.name || '-',
    projectType: it.revision?.project?.type || it.project?.type || '-',
    projectVersion: it.revision?.project?.version || it.project?.version,
    inspectorName: it.inspector?.name || it.inspector?.email || '-',
    files: it.revision?.files || {},
    grade: it.revision?.weighed_grade,
    rating: it.revision?.rating,
    status: it.status,
    updatedAt: it.revision?.updatedAt || it.updatedAt,
    inspection: it // Pass the full inspection object for InspectionStatusBadge
  })), [inspections])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<any>(null)
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false)
  const [isGeneratingReport, setIsGeneratingReport] = useState<string | null>(null)
  const open = Boolean(anchorEl)

  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: string) => {
    const isAsc = sortBy === newOrderBy && sortOrder === 'asc'
    setSortOrder(isAsc ? 'desc' : 'asc')
    setSortBy(newOrderBy)
  }

  // Sorting function that handles different data types
  const getSortValue = (row: any, sortField: string) => {
    const value = row[sortField]

    switch (sortField) {
      case 'updatedAt':
        return new Date(value).getTime()
      case 'organizationName':
      case 'projectName':
      case 'projectType':
      case 'revisionId':
      case 'inspectorName':
      case 'status':
        return (value || '').toString().toLowerCase()
      default:
        return value || ''
    }
  }

  const sortedRows = useMemo(() => {
    const rowsToSort = [...allRows]

    if (sortBy) {
      rowsToSort.sort((a, b) => {
        const aValue = getSortValue(a, sortBy)
        const bValue = getSortValue(b, sortBy)

        if (aValue < bValue) {
          return sortOrder === 'asc' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortOrder === 'asc' ? 1 : -1
        }
        return 0
      })
    }

    return rowsToSort
  }, [allRows, sortBy, sortOrder])

  // Server handles search; display current page (client-sorted for column headers)
  const rows = sortedRows

  const handleClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget)
    setSelectedRow(row)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSelectedRow(null)
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleGenerateUrl = async (revisionId: string) => {
    setIsGeneratingUrl(true)
    try {
      const response = await generateViewerUrl(revisionId)
      if (response.url) {
        // Open URL in new window
        window.open(response.url, '_blank', 'noopener,noreferrer')
        enqueueSnackbar(t('REVISION_MENU_URL_OPENED_SUCCESS'), { variant: 'success' })
      }
    } catch (error) {
      enqueueSnackbar(t('REVISION_MENU_URL_OPENED_ERROR'), { variant: 'error' })
    } finally {
      setIsGeneratingUrl(false)
    }
  }

  const statusStats = useMemo(() => ({
    not_started: statusStatsData?.not_started ?? 0,
    pending: statusStatsData?.pending ?? 0,
    checked: statusStatsData?.checked ?? 0
  }), [statusStatsData])

  return (
    <Box>
      {/* Title Section */}
      <Box sx={{ height: "144px", alignContent: "center", mb: "22px", display: "flex", alignItems: "center" }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 'bold',
            color: '#2F3B49',
          }}
        >
          {t('MY_CHECKS_DASHBOARD_TITLE', { defaultValue: 'My Checks' })}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} xs={12} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatusCard
            backgroundColor="#ACACAC"
            title={t('REVISION_INSPECTION_STATUS_NOT_STARTED')}
            value={statusStats.not_started}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatusCard
            backgroundColor="#ff9800"
            title={t('REVISION_INSPECTION_STATUS_PENDING')}
            value={statusStats.pending}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatusCard
            backgroundColor="#1bb81e"
            title={t('REVISION_INSPECTION_STATUS_CHECKED')}
            value={statusStats.checked}
          />
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Grid container spacing={3} xs={12} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label={t('ADMIN_INSPECTION_SEARCH_REVISION_ID')}
            name="searchQuery"
            value={searchQuery}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                height: '50px'
              }
            }}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('ADMIN_INSPECTION_SEARCH_REVISION_ID')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl sx={{ width: '100%' }}>
            <InputLabel id="status-label">{t('ADMIN_REVISION_JOB_STATUS')}</InputLabel>
            <Select
              labelId="status-label"
              value={status}
              label={t('ADMIN_REVISION_JOB_STATUS')}
              onChange={(e: any) => setStatus(e.target.value)}
              sx={{
                height: '50px'
              }}
            >
              {statusFilterOptions(t).map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center' }}>

        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small" sx={{ fontSize: '14px' }}>
          <TableHead>
            <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
              <CustomTableFontCell sortDirection={sortBy === "updatedAt" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "updatedAt"}
                  direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "updatedAt")}
                >
                  {t('INSPECTION_UPDATED_DATE')}
                  {sortBy === "updatedAt" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "projectName" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "projectName"}
                  direction={sortBy === "projectName" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "projectName")}
                >
                  {t('INSPECTION_PROJECT')}
                  {sortBy === "projectName" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "projectType" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "projectType"}
                  direction={sortBy === "projectType" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "projectType")}
                >
                  {t('PROJECT_FORM_TYPE')}
                  {sortBy === "projectType" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "revisionId" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "revisionId"}
                  direction={sortBy === "revisionId" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "revisionId")}
                >
                  {t('REVISION_REVISION_ID_COLUMN')}
                  {sortBy === "revisionId" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableCell sx={{ cursor: 'default !important' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '14px' }}>
                  {/* {t('ADMIN_REVISION_ACTIONS')} */}
                </Typography>
              </CustomTableCell>
              <CustomTableFontCell sortDirection={sortBy === "inspectorName" ? sortOrder : false}>
                <StyledTableSortLabel
                  active={sortBy === "inspectorName"}
                  direction={sortBy === "inspectorName" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "inspectorName")}
                >
                  {t('BUSINESS_SETTING_MEMBER_PERMISSION_INSPECTOR')}
                  {sortBy === "inspectorName" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sortDirection={sortBy === "status" ? sortOrder : false} sx={{ width: isRtl ? '110px' : '150px', maxWidth: isRtl ? '110px' : '150px' }}>
                <StyledTableSortLabel
                  active={sortBy === "status"}
                  direction={sortBy === "status" ? sortOrder : "asc"}
                  onClick={(event) => handleRequestSort(event, "status")}
                >
                  {t('ADMIN_REVISION_JOB_STATUS')}
                  {sortBy === "status" ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortOrder === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </CustomTableFontCell>
              <CustomTableFontCell sx={{ width: '60px', maxWidth: '60px', paddingLeft: '8px' }}></CustomTableFontCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.id}
                sx={{ cursor: 'pointer', '&:hover': { border: 'none' } }}
                onClick={(e) => {
                  // Prevent click if clicking on buttons/actions
                  if (e.target !== e.currentTarget && !e.currentTarget.contains(e.target as Node)) {
                    return
                  }
                  setSelectedRow(r)
                }}
              >
                {/* Updated Date */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }}>
                  {moment.utc(r.updatedAt).local().format('DD/MM/YY')}
                  <span className='time' style={{ marginLeft: '10px' }}></span>
                  {moment.utc(r.updatedAt).local().format('HH:mm')}
                </CustomTableCell>

                {/* Project */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }}>
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.projectName}</Typography>
                </CustomTableCell>

                {/* Project Type */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }}>
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.projectType}</Typography>
                </CustomTableCell>

                {/* Revision ID */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }} dir="ltr">
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.revisionId}</Typography>
                </CustomTableCell>

                {/* Actions - Grouped Icons */}
                <CustomTableCell sx={{ cursor: 'default !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', }}>
                    {/* Left side - Action Icons */}
                    <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                      {/* Download Full Report */}
                      {r.files?.full && (
                        <ButtonIconSvg
                          onClick={() => {
                            window.open(`${r.files.full.url}?authorization=${auth?.token}`, '_blank')
                          }}
                        >
                          <FileDownloadOutlinedIcon sx={{ fontSize: '36px' }} />
                        </ButtonIconSvg>
                      )}

                      {/* 3D Viewer */}
                      {r.files?.idf && (
                        <NewFeatureBadge>
                          <ButtonIconSvg
                            onClick={() => {
                              const code = btoa(String(r.revisionId))
                              const viewerUrl = `/3d-viewer?code=${code}&revisionId=${r.revisionId}&projectId=${r.projectId}`
                              window.open(viewerUrl, '_blank')
                            }}
                          >
                            <ThreeDIcon />
                          </ButtonIconSvg>
                        </NewFeatureBadge>
                      )}

                      {/* SHGC / WWR Viewer */}
                      {isShgcViewerEnabled(features, r.projectVersion, r.updatedAt) && (
                        <NewFeatureBadge>
                          <ButtonIconSvg
                            onClick={() => {
                              const code = btoa(String(r.revisionId))
                              const viewerUrl = `/shgc-viewer?code=${code}&revisionId=${r.revisionId}&projectId=${r.projectId}`
                              window.open(viewerUrl, '_blank')
                            }}
                          >
                            <span style={{ fontSize: '10px', fontWeight: 700 }}>{t('SHGC_VIEWER_BUTTON')}</span>
                          </ButtonIconSvg>
                        </NewFeatureBadge>
                      )}

                      {/* CV Viewer */}
                      {isCvViewerEnabled(features, r.projectVersion, r.updatedAt) && (
                        <NewFeatureBadge>
                          <ButtonIconSvg
                            onClick={() => {
                              const code = btoa(String(r.revisionId))
                              const viewerUrl = `/cv-viewer?code=${code}&revisionId=${r.revisionId}&projectId=${r.projectId}`
                              window.open(viewerUrl, '_blank')
                            }}
                          >
                            <CvIcon style={{ width: 28, height: 28 }} />
                          </ButtonIconSvg>
                        </NewFeatureBadge>
                      )}

                      {/* IDF Viewer - Generate URL */}
                      {r.files?.idf && (
                        <ButtonIconSvg
                          onClick={() => {
                            handleGenerateUrl(r.revisionId)
                          }}
                        >
                          <ViewWeekOutlinedIcon sx={{ fontSize: '36px' }} />
                        </ButtonIconSvg>
                      )}

                      {/* Validation Report */}
                      <ButtonIconSvg
                        onClick={async () => {
                          if (isGeneratingReport === r.revisionId) return
                          setIsGeneratingReport(r.revisionId)
                          try {
                            await downloadValidationReport(r.revisionId)
                            enqueueSnackbar(t('VALIDATION_REPORT_DOWNLOADED_SUCCESS', { defaultValue: 'Validation report downloaded successfully' }), { variant: 'success' })
                          } catch (error) {
                            enqueueSnackbar(t('VALIDATION_REPORT_DOWNLOAD_ERROR', { defaultValue: 'Error downloading validation report' }), { variant: 'error' })
                          } finally {
                            setIsGeneratingReport(null)
                          }
                        }}
                      >
                        <DescriptionIcon sx={{ fontSize: '36px' }} />
                      </ButtonIconSvg>

                      {/* Report JSON Viewer */}
                      <ButtonIconSvg
                        onClick={() => {
                          startTransition(() => {
                            navigate(`/report/${r.revisionId}`)
                          })
                        }}
                      >
                        <CodeIcon sx={{ fontSize: '36px' }} />
                      </ButtonIconSvg>
                    </Box>
                  </Box>
                </CustomTableCell>

                {/* Inspector Name */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text' }}>
                  <Typography variant="body2" sx={{ fontSize: '14px' }}>{r.inspectorName || '-'}</Typography>
                </CustomTableCell>

                {/* Status */}
                <CustomTableCell sx={{ cursor: 'default !important', userSelect: 'text', width: '135px', maxWidth: '135px' }} >
                  <InspectionStatusBadge rtl={isRtl} inspection={r.inspection} />
                </CustomTableCell>
                <CustomTableCell sx={{ cursor: 'default !important' }}>
                  <ButtonIcon
                    isActive={selectedRow?.id === r.id ? true : false}
                    onClick={(e) => handleClick(e, r)}
                    size="small"
                  >
                    <MoreHorizIcon />
                  </ButtonIcon>
                </CustomTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Menu
        sx={{ fontSize: 14 }}
        anchorEl={anchorEl}
        open={open && selectedRow}
        onClose={handleClose}
      >
        {/* Additional Downloads */}
        {selectedRow?.files?.full && (
          <a
            key="full"
            href={`${selectedRow.files.full.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>{t('REVISION_MENU_DOWNLOAD_DETAILED_REPORT')}</MenuItem>
          </a>
        )}
        {selectedRow?.files?.dxf && (
          <a
            key="dxf"
            href={`${selectedRow.files.dxf.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>{t('REVISION_MENU_DOWNLOAD_3D_REPORT')}</MenuItem>
          </a>
        )}
        {selectedRow?.files?.h_report && (
          <a
            key="h_report"
            href={`${selectedRow.files.h_report.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>{t('REVISION_MENU_DOWNLOAD_H_REPORT')}</MenuItem>
          </a>
        )}
        {selectedRow?.files?.report_1045 && (
          <a
            key="report_1045"
            href={`${selectedRow.files.report_1045.url}?authorization=${auth?.token}`}
            rel='noopener noreferrer'
            download
            className='dropdown-menu__link'
          >
            <MenuItem>{t('REVISION_MENU_DOWNLOAD_1045_REPORT')}</MenuItem>
          </a>
        )}
      </Menu>
    </Box>
  )
}

